import React, { useEffect, useState } from "react";
import styled from "@emotion/styled/macro";
import {
  Button,
  Typography,
  tokens,
  GradientBar,
  SvgNames,
  SvgIcon,
  UtilityConnectionStatusPill,
} from "@sunrun/experience-ui-components";
import { ConfirmInfoTaskCard } from "components/molecules/ConfirmInfo/ConfirmInfoTaskCard";
import { UsageResponse } from "providers/Types";
import { calculateUsageData } from "utils/usageUtils";

const UtilityTask = ({
  usage,
  onOpen,
  prospectId,
  intervalDataStatus,
}: {
  usage: UsageResponse;
  onOpen: () => void;
  prospectId: string;
  intervalDataStatus?: string | null;
}) => {
  const [attachments, setAttachments] = useState<any[]>([]);
  const {
    attachments: attachmentsData,
    isLoading: isUsageLoading,
    prospect,
  } = usage;

  useEffect(() => {
    if (Array.isArray(attachmentsData) && attachmentsData.length) {
      setAttachments(attachmentsData);
    }
  }, [attachmentsData]);

  const { totalUsage, monthsEntered } = calculateUsageData({ prospect });
  const hasAttachments = attachments.length > 0;

  return (
    <ConfirmInfoTaskCard
      taskHeader="3. Utility info & utility bill"
      subHeader="Please upload customer's utility documents and enter the customer's kWh usage from their bill here"
      taskAction={
        <Button
          onClick={() => onOpen()}
          color="secondary"
          size="xs"
          style={{ height: "fit-content" }}
        >
          Edit
        </Button>
      }
      loading={isUsageLoading}
    >
      <ContentContainer>
        <Column>
          <Section>
            <IconContainer>
              <SvgIcon
                name={SvgNames.FileOutline}
                color={tokens.BRAND_HERO_BLUE}
                height={24}
                width={24}
              />
            </IconContainer>
            <InfoContainer>
              <SectionTitle className="section-title">
                Utility bill
              </SectionTitle>
              <StatusContainer>
                <StatusText isUploaded={hasAttachments}>
                  {hasAttachments ? "Uploaded" : "Not Uploaded"}
                </StatusText>
              </StatusContainer>
            </InfoContainer>
          </Section>
          <Section>
            <StyledPill status={intervalDataStatus || ""} />
          </Section>
        </Column>

        <Divider />

        <RightSection>
          <IconContainer>
            <SvgIcon
              name={SvgNames.TrendingUpSvg}
              color={tokens.BRAND_HERO_BLUE}
              height={24}
              width={24}
            />
          </IconContainer>
          <InfoContainer>
            <SectionTitle className="section-title">Usage</SectionTitle>
            <UsageInfo>
              <UsageRow>
                <Typography color={tokens.TINTS_OFF_WHITE_10} size={12}>
                  Total usage entered:{" "}
                </Typography>
                <UsageValue monthsEntered={monthsEntered}>
                  {monthsEntered > 0 ? `${totalUsage} kWh` : "Not entered"}
                </UsageValue>
              </UsageRow>
              <UsageRow>
                <Typography
                  color={tokens.TINTS_OFF_WHITE_10}
                  size={14}
                  fontWeight="medium"
                >
                  Estimated annual usage:{" "}
                </Typography>
                <UsageValue monthsEntered={monthsEntered}>
                  {monthsEntered > 0
                    ? monthsEntered === 12
                      ? `${totalUsage} kWh`
                      : "See in IHD"
                    : "Not entered"}
                </UsageValue>
              </UsageRow>
            </UsageInfo>

            <StyledGradientBar
              midPoint={(monthsEntered / 12) * 100}
              height={8}
              borderRadius={4}
              className="gradient-bar"
            >
              <Typography color={tokens.WHITE} size={10}>
                Months {monthsEntered}/12 entered
              </Typography>
            </StyledGradientBar>
          </InfoContainer>
        </RightSection>
      </ContentContainer>
    </ConfirmInfoTaskCard>
  );
};

const ContentContainer = styled.div`
  margin-top: 20px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
`;

const Section = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  gap: 12px;
`;

const RightSection = styled(Section)`
  flex: 2;
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Divider = styled.div`
  width: 2px;
  background-color: ${tokens.HEROBLUE_80};
  max-height: 100%;
  height: 80px;
  min-height: 80px;
  margin: auto;
  border-radius: 2px;
`;

const SectionTitle = styled(Typography)`
  &.section-title {
    font-family: Roobert;
    font-weight: 400;
    font-style: Regular;
    font-size: 12px;
    leading-trim: NONE;
    line-height: 100%;
    letter-spacing: 0%;
    margin-right: auto !important;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${tokens.CHARGEBLUE_90};
  width: fit-content;
  height: fit-content;
  padding: 4px;
`;

const InfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-direction: column;
`;

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatusText = styled.span<{ isUploaded: boolean }>`
  color: ${(props) =>
    props.isUploaded ? tokens.BRAND_HERO_BLUE : tokens.WARNING_40};
  font-weight: 500;
  font-size: 12px;
`;

const UsageInfo = styled.div`
  margin-bottom: 0px;
`;

const UsageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const UsageValue = styled.span<{ monthsEntered: number }>`
  font-weight: 600;
  font-size: 12px;
  color: ${({ monthsEntered }) =>
    monthsEntered > 0 ? tokens.BRAND_HERO_BLUE : tokens.WARNING_40};
`;

const StyledGradientBar = styled(GradientBar)`
  &.gradient-bar {
    padding: 8px 20px;
    border-radius: 8px;
  }
  margin-right: auto;
`;

const StyledPill = styled(UtilityConnectionStatusPill)`
  width: 120px !important;
  white-space: wrap !important;
  word-break: break-word !important;
  padding: 8px 16px !important;
  p {
    line-height: 16px !important;
  }
`;

export { UtilityTask };
